import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import {
  Box,
  Typography,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  selectFacilitySignupSelectedFacilityTypeId
} from '../../../modules/facility/selectors';
import { unwrapResult } from '@reduxjs/toolkit';
import heroImage from '../../../assets/facility/signup-background.jpg';
import Logo from '../../../assets/main/logo-white.png'
import UploadIcon from '../../../assets/facility/upload-icon.svg'

import {
  PageContainer,
  HeroPanel,
  HeroImage,
  HeroBranding,
  FormPanel,
  FormContainer,
  StyledStepper,
  SubmitButton,
  FormBox,
  UploadContainer,
  StyledStepLable,
} from './styles';

import { StepIconProps } from "@mui/material/StepIcon";
import { fetchFacilityTypeDocumentRequirements, uploadFacilityDocument } from '../../../modules/facility/actions';
import {
  selectFacilityTypeDocumentRequirements,
  selectFacilityTypeDocumentRequirementsLoading,
  selectFacilityTypeDocumentRequirementsError,
  selectFacilityDocumentUploadLoading
} from '../../../modules/facility/selectors';
import { logout } from '../../../modules/login/reducers';
import { AppDispatch } from '../../../store';
import { IFacilityTypeDocumentRequirement } from '../../../modules/facility/types';
import AlertPopup from '../../../components/common/AlertPopup';


interface FileUploadState {
  [key: number]: File | null; // key is document_type_id
}

const steps = ['Basic Details', 'Verification Documents'];

const FacilitySignupSecondPage: React.FC = () => {
  const activeStep: number = 1;
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadState>({});
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);
  const inputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const facilityTypeId = useSelector(selectFacilitySignupSelectedFacilityTypeId);

  const documentRequirements = useSelector(selectFacilityTypeDocumentRequirements);
  const isLoadingRequirements = useSelector(selectFacilityTypeDocumentRequirementsLoading);
  const errorRequirements = useSelector(selectFacilityTypeDocumentRequirementsError);

  const isUploading = useSelector(selectFacilityDocumentUploadLoading);

  useEffect(() => {
    if (facilityTypeId) {
      dispatch(fetchFacilityTypeDocumentRequirements({ facility_type_id: facilityTypeId }));
    }
  }, [dispatch, facilityTypeId, navigate]);



  const handleFileChange = (documentTypeId: number) => (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setUploadedFiles((prev) => ({
        ...prev,
        [documentTypeId]: event.target.files![0],
      }));
    }
  };

  const handleClick = (documentTypeId: number) => () => {
    inputRefs.current[documentTypeId]?.click();
  };

  const handleSubmit = async (): Promise<void> => {
    // if (!documentRequirements) return;
    if (!documentRequirements || documentRequirements.length === 0) {
      setAlert({ message: 'Facility signup is completed, please check your email inbox for the login credentials', severity: 'success' });
      setTimeout(() => {
        dispatch(logout());
        navigate('/');
      }, 2000);
      return;
    }

    if (Object.keys(uploadedFiles).length !== documentRequirements.length) {
      setAlert({ message: 'Please upload all required documents.', severity: 'error' });
      return;
    }
    setAlert(null);

    let allUploadsSuccessful = true;
    for (const doc of documentRequirements) {
      const file = uploadedFiles[doc.document_type.document_id];
      if (file) {
        try {
          await dispatch(uploadFacilityDocument({
            document: file,
            document_name: doc.document_type.document_type,
            document_type: doc.requirement_id,
          })).then(unwrapResult);
        } catch (err: any) {
          setAlert({ message: err || 'Failed to upload document.', severity: 'error' });
          allUploadsSuccessful = false;
          break;
        }
      }
    }

    if (allUploadsSuccessful) {
      setAlert({ message: 'Documents uploaded successfully! Redirecting to login...', severity: 'success' });
      setTimeout(() => {
        dispatch(logout());
        navigate('/');
      }, 2000);
    }
  };

  const CustomStepIcon = (props: StepIconProps) => {
    return <StyledStepLable active={props.active}>{props.icon}</StyledStepLable>;
  };

  return (
    <PageContainer>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      {/* Left Panel - Hero Image */}
      <HeroPanel>
        <HeroImage src={heroImage} alt="Healthcare professional" />
        <HeroBranding>
          <Box>
            <img src={Logo} alt="Facility signup logo" />
          </Box>
        </HeroBranding>
      </HeroPanel>

      {/* Right Panel - Form */}
      <FormPanel>
        <FormContainer>
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" gutterBottom>
              Register Your Facility
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create your account to post shifts, manage workers, and streamline
              your facility's staffing process
            </Typography>
          </Box>

          {/* Stepper */}
          <StyledStepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  StepIconComponent={(props) =>
                    props.completed ? <CustomStepIcon {...props} /> : <CustomStepIcon {...props} />
                  }
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </StyledStepper>

          {/* Form */}
          {activeStep === 1 && (
            <FormBox>
              {isLoadingRequirements && <CircularProgress />}
              {errorRequirements && <Alert severity="error">{errorRequirements}</Alert>}

              {documentRequirements && documentRequirements.length > 0 ? (
                documentRequirements.map((doc: IFacilityTypeDocumentRequirement) => (
                  <Box key={doc.requirement_id} mb={3}>
                    <Typography variant='body1'>
                      {doc.document_type.document_type}
                    </Typography>
                    <UploadContainer onClick={handleClick(doc.document_type.document_id)} mb={2}>
                      <img src={UploadIcon} alt='Facility upload document' />
                      <Typography variant="body1" color="text.secondary">
                        Drag & Drop or Click to Upload (.pdf, .jpg, .png)
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {uploadedFiles[doc.document_type.document_id]?.name || 'No File Uploaded'}
                      </Typography>
                      <input
                        type="file"
                        hidden
                        ref={(el) => { inputRefs.current[doc.document_type.document_id] = el; }}
                        accept=".pdf,.jpg,.png"
                        onChange={handleFileChange(doc.document_type.document_id)}
                      />
                    </UploadContainer>
                  </Box>
                ))
              ) : (
                <Box mb={3}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No documents are required please click on the submit button to complete the signup
                  </Alert>
                </Box>
              )}

              <SubmitButton
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={isLoadingRequirements || isUploading}
              >
                {isUploading ? <CircularProgress size={24} /> : 'Submit & Register'}
              </SubmitButton>
            </FormBox>
          )}

        </FormContainer>
      </FormPanel>
    </PageContainer>
  );
};

export default FacilitySignupSecondPage;
